import { useState, useEffect, useRef } from 'react';
import * as THREE from 'three';
import './ParameterHotspots.css';

export interface ParameterWithPosition {
  name: string;
  displayName: string;
  value: string | boolean | number | string[] | number[] | boolean[];
  defaultValue: string | boolean | number | string[] | number[] | boolean[];
  type?: string;
  description?: string;
  group?: string;
  range?: { min?: number; max?: number; step?: number };
  options?: { value: string | number; label: string }[];
  // Position in 3D space (will be projected to 2D)
  position3D?: { x: number; y: number; z: number };
  // Which dimension this parameter represents
  dimension?: 'width' | 'height' | 'depth' | 'radius' | 'diameter' | 'length' | 'thickness' | 'size' | 'other';
}

interface ParameterHotspotsProps {
  parameters: ParameterWithPosition[];
  canvasRef: React.RefObject<HTMLCanvasElement>;
  camera: THREE.Camera | null;
  modelBounds: THREE.Box3 | null;
  onParameterChange: (name: string, value: number | string | boolean) => void;
  onApplyChange: (name: string, value: number | string | boolean) => void;
  isVisible?: boolean;
}

interface Hotspot {
  parameter: ParameterWithPosition;
  screenPosition: { x: number; y: number };
  isVisible: boolean;
}

interface EditingState {
  parameter: ParameterWithPosition;
  position: { x: number; y: number };
  inputValue: string;
}

/**
 * ParameterHotspots - Interactive dots positioned on the 3D model for parameter editing
 * 
 * Dots are positioned on the model surface based on parameter semantics.
 * Clicking a dot opens a simple inline editor.
 */
export const ParameterHotspots: React.FC<ParameterHotspotsProps> = ({
  parameters,
  canvasRef,
  camera: _camera,
  modelBounds,
  onParameterChange,
  onApplyChange,
  isVisible = true,
}) => {
  void _camera;
  const [hotspots, setHotspots] = useState<Hotspot[]>([]);
  const [editing, setEditing] = useState<EditingState | null>(null);
  const [showHotspots, setShowHotspots] = useState(true);
  const containerRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);

  // Calculate positions based on model bounds and parameter semantics
  const updateHotspots = () => {
    if (!canvasRef.current || parameters.length === 0) {
      setHotspots([]);
      return;
    }

    // Only show numeric parameters (dimensions that can be adjusted)
    const numericParams = parameters.filter(p => 
      p.type === 'number' || typeof p.value === 'number'
    );

    if (numericParams.length === 0) {
      setHotspots([]);
      return;
    }

    // Calculate center position based on model bounds
    const centerX = 50;
    const centerY = 50;

    // Position hotspots based on parameter semantics, clustered near center
    const newHotspots = numericParams.map((param, index) => {
      const nameLower = param.name.toLowerCase();
      let xPercent = centerX;
      let yPercent = centerY;
      
      // Tight positioning around model center with slight offsets based on semantics
      const offset = 8; // Small offset from center
      
      if (nameLower.includes('width') || nameLower.includes('x') || nameLower === 'w') {
        // Width: slightly right of center
        xPercent = centerX + offset;
        yPercent = centerY;
      } else if (nameLower.includes('height') || nameLower.includes('z') || nameLower === 'h') {
        // Height: slightly above center
        xPercent = centerX;
        yPercent = centerY - offset;
      } else if (nameLower.includes('depth') || nameLower.includes('length') || nameLower.includes('y') || nameLower === 'd' || nameLower === 'l') {
        // Depth: slightly below-right of center
        xPercent = centerX + offset * 0.7;
        yPercent = centerY + offset * 0.7;
      } else if (nameLower.includes('radius') || nameLower === 'r') {
        // Radius: diagonal from center
        xPercent = centerX + offset * 0.8;
        yPercent = centerY - offset * 0.5;
      } else if (nameLower.includes('thick') || nameLower.includes('wall')) {
        // Thickness: slightly left of center
        xPercent = centerX - offset;
        yPercent = centerY;
      } else if (nameLower.includes('bore') || nameLower.includes('hole')) {
        // Bore/hole: center
        xPercent = centerX;
        yPercent = centerY + offset * 0.5;
      } else {
        // Other parameters: distribute in a small circle around center
        const angleStep = (Math.PI * 2) / Math.max(numericParams.length, 6);
        const baseAngle = index * angleStep - Math.PI / 2; // Start from top
        xPercent = centerX + Math.cos(baseAngle) * offset;
        yPercent = centerY + Math.sin(baseAngle) * offset;
      }

      // Generate display name
      const displayName = param.displayName || param.name
        .replace(/_/g, ' ')
        .replace(/([a-z])([A-Z])/g, '$1 $2')
        .split(' ')
        .map(word => word.charAt(0).toUpperCase() + word.slice(1))
        .join(' ');

      return {
        parameter: { ...param, displayName },
        screenPosition: { x: xPercent, y: yPercent },
        isVisible: true,
      };
    });

    setHotspots(newHotspots);
  };

  // Run updateHotspots when dependencies change
  useEffect(() => {
    if (!isVisible || !showHotspots || parameters.length === 0) return;
    
    const timeoutId = setTimeout(() => {
      updateHotspots();
    }, 100);
    
    const handleResize = () => updateHotspots();
    window.addEventListener('resize', handleResize);
    return () => {
      window.removeEventListener('resize', handleResize);
      clearTimeout(timeoutId);
    };
  }, [parameters, canvasRef, isVisible, showHotspots, modelBounds]);

  // Focus input when editing starts
  useEffect(() => {
    if (editing && inputRef.current) {
      inputRef.current.focus();
      // Note: setSelectionRange doesn't work on number inputs, so just focus
    }
  }, [editing]);

  const handleHotspotClick = (hotspot: Hotspot, event: React.MouseEvent) => {
    event.stopPropagation();
    
    // If already editing this parameter, close it
    if (editing?.parameter.name === hotspot.parameter.name) {
      setEditing(null);
      return;
    }

    setEditing({
      parameter: hotspot.parameter,
      position: { ...hotspot.screenPosition },
      inputValue: String(hotspot.parameter.value),
    });
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (!editing) return;
    const newValue = e.target.value;
    setEditing({
      ...editing,
      inputValue: newValue,
    });
    
    // Live preview update
    const numValue = parseFloat(newValue);
    if (!isNaN(numValue)) {
      onParameterChange(editing.parameter.name, numValue);
    }
  };

  const handleInputKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter') {
      applyEdit();
    } else if (e.key === 'Escape') {
      cancelEdit();
    }
    // Don't stop propagation for other keys - allow normal typing
  };

  const applyEdit = () => {
    if (!editing) return;
    
    const numValue = parseFloat(editing.inputValue);
    if (!isNaN(numValue)) {
      onApplyChange(editing.parameter.name, numValue);
    }
    setEditing(null);
  };

  const cancelEdit = () => {
    if (!editing) return;
    
    // Revert to original value (safely handle non-numeric values)
    const originalValue = editing.parameter.value;
    if (typeof originalValue === 'number') {
      onParameterChange(editing.parameter.name, originalValue);
    } else if (typeof originalValue === 'string') {
      const numValue = parseFloat(originalValue);
      if (!isNaN(numValue)) {
        onParameterChange(editing.parameter.name, numValue);
      }
    }
    setEditing(null);
  };

  // Close editor when clicking outside
  useEffect(() => {
    if (!editing) return;
    
    const handleClickOutside = (e: MouseEvent) => {
      if (containerRef.current && !containerRef.current.contains(e.target as Node)) {
        // Apply the current value
        const numValue = parseFloat(editing.inputValue);
        if (!isNaN(numValue)) {
          onApplyChange(editing.parameter.name, numValue);
        }
        setEditing(null);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [editing, onApplyChange]);

  if (!isVisible || hotspots.length === 0) {
    return null;
  }

  return (
    <>
      {/* Toggle Button */}
      <button
        className="hotspots-toggle-btn"
        onClick={() => setShowHotspots(!showHotspots)}
        title={showHotspots ? 'Hide parameter controls' : 'Show parameter controls'}
      >
        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
          {showHotspots ? (
            <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z M12 9a3 3 0 100 6 3 3 0 000-6z" strokeLinecap="round" strokeLinejoin="round"/>
          ) : (
            <path d="M17.94 17.94A10.07 10.07 0 0112 20c-7 0-11-8-11-8a18.45 18.45 0 015.06-5.94M9.9 4.24A9.12 9.12 0 0112 4c7 0 11 8 11 8a18.5 18.5 0 01-2.16 3.19m-6.72-1.07a3 3 0 11-4.24-4.24 M1 1l22 22" strokeLinecap="round" strokeLinejoin="round"/>
          )}
        </svg>
      </button>

      {showHotspots && (
        <div ref={containerRef} className="parameter-hotspots-container">
          {hotspots.map((hotspot, index) => (
            hotspot.isVisible && (
              <div
                key={hotspot.parameter.name}
                className={`parameter-hotspot ${editing?.parameter.name === hotspot.parameter.name ? 'active' : ''}`}
                style={{
                  left: `${hotspot.screenPosition.x}%`,
                  top: `${hotspot.screenPosition.y}%`,
                  animationDelay: `${index * 0.15}s`,
                }}
                onClick={(e) => handleHotspotClick(hotspot, e)}
              >
                <div className="hotspot-dot" />
                <div className="hotspot-pulse" />
                
                {/* Simple label showing parameter name and value */}
                <div className="hotspot-label">
                  <span className="label-name">{hotspot.parameter.displayName}</span>
                  <span className="label-value">{hotspot.parameter.value}</span>
                </div>
              </div>
            )
          ))}

          {/* Simple Inline Editor */}
          {editing && (
            <div
              className="parameter-inline-editor"
              style={{
                left: `${editing.position.x}%`,
                top: `${editing.position.y}%`,
              }}
            >
              <div className="inline-editor-content">
                <span className="editor-param-name">{editing.parameter.displayName}</span>
                <div className="editor-input-wrapper">
                  <input
                    ref={inputRef}
                    type="number"
                    className="inline-editor-input"
                    value={editing.inputValue}
                    onChange={handleInputChange}
                    onKeyDown={handleInputKeyDown}
                    min={editing.parameter.range?.min}
                    max={editing.parameter.range?.max}
                    step={editing.parameter.range?.step || 1}
                  />
                  <span className="editor-unit">mm</span>
                </div>
                <div className="editor-buttons">
                  <button className="editor-btn-apply" onClick={applyEdit}>✓</button>
                  <button className="editor-btn-cancel" onClick={cancelEdit}>✕</button>
                </div>
              </div>
            </div>
          )}
        </div>
      )}
    </>
  );
};

export default ParameterHotspots;
